import React, { useState, useRef, useEffect } from 'react';
import axios from 'axios';
import browserHistory from '../../history';
import Header from '../authentication/Header';
import { getEnvoiConfig } from '../lib/envoiConfig';
import { fetchTwelveLabsEntities, resolveTwelveLabsIds } from '../lib/twelvelabs';
import HoverClipPreview from './HoverClipPreview';
import './HeirloomAISearchPage.css';

// Hidden per product decision — keep the image-upload / @ Entity mention
// feature intact underneath so it can be re-enabled by flipping this flag.
const SHOW_IMAGE_AND_ENTITY_SEARCH = false;

const AI_MODELS = [
    { platform: 'Claude',     models: ['Opus 4.8', 'Opus 4.7'] },
    { platform: 'TwelveLabs', models: ['Marengo 3.0', 'Pegasus 1.5'] },
];

const LANGUAGES = {
    'ar-SA': 'Modern Standard Arabic',
    'de-DE': 'German',
    'en-AU': 'Australian English',
    'en-GB': 'British English',
    'en-IN': 'Indian English',
    'en-US': 'US English',
    'es-ES': 'Spanish',
    'es-US': 'US Spanish',
    'fr-CA': 'Canadian French',
    'fr-FR': 'French',
    'hi-IN': 'Indian Hindi',
    'it-IT': 'Italian',
    'kn-IN': 'Kannada',
    'ko-KR': 'Korean',
    'ml-IN': 'Malayalam',
    'pt-BR': 'Brazilian Portuguese',
    'ta-IN': 'Tamil',
    'te-IN': 'Telugu',
};

const emptyFilter = () => ({
    id: [],
    duration: { min: '', max: '' },
    width: { useRange: false, value: '', min: '', max: '' },
    height: { useRange: false, value: '', min: '', max: '' },
    size: { useRange: false, value: '', min: '', max: '' },
    filename: '',
});

function getCategories() {
    return ['Movies', 'TV Shows', 'Music Videos', 'Interviews', 'Commercials', 'Podcasts'];
}

function formatTime(sec) {
    const m = Math.floor(sec / 60);
    const s = Math.floor(sec % 60);
    return `${m}:${String(s).padStart(2, '0')}`;
}

/* ── Dual-thumb range slider (native range inputs, no external deps) ── */
function DualRangeSlider({ min, max, value, onChange }) {
    const [lo, hi] = value;
    const pct = (v) => ((v - min) / (max - min)) * 100;

    return (
        <div className="ai-adv-range-slider">
            <div className="ai-adv-range-track">
                <div
                    className="ai-adv-range-fill"
                    style={{ left: `${pct(lo)}%`, width: `${pct(hi) - pct(lo)}%` }}
                />
            </div>
            <input
                type="range"
                min={min}
                max={max}
                value={lo}
                className="ai-adv-range-input"
                onChange={(e) => onChange([Math.min(Number(e.target.value), hi), hi])}
            />
            <input
                type="range"
                min={min}
                max={max}
                value={hi}
                className="ai-adv-range-input"
                onChange={(e) => onChange([lo, Math.max(Number(e.target.value), lo)])}
            />
        </div>
    );
}

// Module-level cache (survives unmount/remount within the same app session,
// e.g. navigating to the full video player page and back) so a prior search
// doesn't have to be re-run when the user returns to this page.
let cachedSearchState = null;

export default function HeirloomAISearchPage() {
    const { lambdaUrl, appname, environmentVariable, defaultLandscape } = getEnvoiConfig();

    const [toastMsg, setToastMsg] = useState(null);
    const showToast = (message, type = 'info') => {
        setToastMsg({ message, type });
        setTimeout(() => setToastMsg(null), 3000);
    };

    const [query, setQuery]               = useState(() => cachedSearchState?.query ?? '');
    const [categories]                    = useState(() => getCategories());
    const [selectedCats, setCats]         = useState(() => cachedSearchState?.selectedCats ?? []);
    const [selectedModels, setModels]     = useState(() => cachedSearchState?.selectedModels ?? []);
    const [selectedLanguages, setLanguages] = useState(() => cachedSearchState?.selectedLanguages ?? []);
    const [categoryOpen, setCategoryOpen] = useState(false);
    const [modelOpen, setModelOpen]       = useState(false);
    const [languageOpen, setLanguageOpen] = useState(false);
    const [isListening, setIsListening]   = useState(false);
    const [micSupported, setMicSupported] = useState(true);
    const [loading, setLoading]           = useState(false);
    const [results, setResults]           = useState(() => cachedSearchState?.results ?? []);
    const [pageInfo, setPageInfo]         = useState(() => cachedSearchState?.pageInfo ?? null);
    const [searchError, setSearchError]   = useState('');
    const [hasSearched, setHasSearched]   = useState(() => cachedSearchState?.hasSearched ?? false);
    const [resolvingId, setResolvingId]   = useState(null);
    const [hoveredKey, setHoveredKey]     = useState(null);
    const hoverTimerRef = useRef(null);
    const [menuOpenKey, setMenuOpenKey]   = useState(null);

    const HOVER_PLAY_DELAY_MS = 400;

    // Keep the module-level cache in sync so results survive navigating away
    // (e.g. to the full video player page) and back to this page.
    useEffect(() => {
        cachedSearchState = { query, selectedCats, selectedModels, selectedLanguages, results, pageInfo, hasSearched };
    }, [query, selectedCats, selectedModels, selectedLanguages, results, pageInfo, hasSearched]);

    useEffect(() => {
        if (!menuOpenKey) return;
        const closeMenu = () => setMenuOpenKey(null);
        document.addEventListener('click', closeMenu);
        return () => document.removeEventListener('click', closeMenu);
    }, [menuOpenKey]);

    const handleThumbMouseEnter = (itemKey) => {
        if (hoverTimerRef.current) clearTimeout(hoverTimerRef.current);
        hoverTimerRef.current = setTimeout(() => {
            setHoveredKey(itemKey);
            hoverTimerRef.current = null;
        }, HOVER_PLAY_DELAY_MS);
    };

    const handleThumbMouseLeave = (itemKey) => {
        if (hoverTimerRef.current) {
            clearTimeout(hoverTimerRef.current);
            hoverTimerRef.current = null;
        }
        setHoveredKey((k) => (k === itemKey ? null : k));
    };

    useEffect(() => () => {
        if (hoverTimerRef.current) clearTimeout(hoverTimerRef.current);
    }, []);

    // Image search
    const [searchImages, setSearchImages] = useState([]);
    const [showImagesModal, setShowImagesModal] = useState(false);

    // @ Entity mention
    const [entityOpen, setEntityOpen]           = useState(false);
    const [entityMentionStart, setEntityMentionStart] = useState(null);
    const [entities, setEntities]               = useState([]);
    const [entitiesLoading, setEntitiesLoading] = useState(false);

    // Advanced search
    const [advancedOpen, setAdvancedOpen]             = useState(false);
    const [filterOpen, setFilterOpen]                 = useState(true);
    const [searchOptions, setSearchOptions]           = useState({ visual: true, audio: true, transcription: true });
    const [transcriptionOptions, setTranscriptionOptions] = useState({ lexical: true, semantic: true });
    const [operator, setOperator]                     = useState('or');
    const [groupBy, setGroupBy]                       = useState('clip');
    const [pageLimit, setPageLimit]                   = useState(10);
    const [rankRange, setRankRange]                   = useState([0, 100]);
    const [filter, setFilter]                         = useState(() => emptyFilter());

    const categoryRef    = useRef(null);
    const modelRef       = useRef(null);
    const languageRef    = useRef(null);
    const entityRef      = useRef(null);
    const inputRef       = useRef(null);
    const recognitionRef = useRef(null);

    useEffect(() => {
        const handler = (e) => {
            if (categoryRef.current && !categoryRef.current.contains(e.target)) setCategoryOpen(false);
            if (modelRef.current && !modelRef.current.contains(e.target)) setModelOpen(false);
            if (languageRef.current && !languageRef.current.contains(e.target)) setLanguageOpen(false);
            if (entityRef.current && !entityRef.current.contains(e.target)) {
                setEntityOpen(false);
                setEntityMentionStart(null);
            }
        };
        document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, []);

    useEffect(() => {
        const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
        if (!SR) { setMicSupported(false); return; }

        const rec = new SR();
        rec.continuous     = false;
        rec.interimResults = true;
        rec.lang           = 'en-US';

        rec.onresult = (event) => {
            const transcript = Array.from(event.results).map(r => r[0].transcript).join('');
            setQuery(transcript);
        };
        rec.onend   = () => setIsListening(false);
        rec.onerror = () => setIsListening(false);

        recognitionRef.current = rec;
        return () => recognitionRef.current && recognitionRef.current.abort();
    }, []);

    const toggleMic = () => {
        if (!recognitionRef.current) return;
        if (isListening) {
            recognitionRef.current.stop();
            setIsListening(false);
        } else {
            setQuery('');
            setCategoryOpen(false);
            setModelOpen(false);
            recognitionRef.current.start();
            setIsListening(true);
            setTimeout(() => inputRef.current && inputRef.current.focus(), 50);
        }
    };

    const toggleCat      = (c) => setCats(p      => p.includes(c) ? p.filter(x => x !== c) : [...p, c]);
    const toggleModel    = (m) => setModels(p    => p.includes(m) ? p.filter(x => x !== m) : [...p, m]);
    const toggleLanguage = (l) => setLanguages(p => p.includes(l) ? p.filter(x => x !== l) : [...p, l]);

    /* ── @ Entity mention ── */
    const entityFilterText = entityMentionStart != null ? query.slice(entityMentionStart + 1) : '';

    useEffect(() => {
        if (!entityOpen) return;
        setEntitiesLoading(true);
        const handle = setTimeout(() => {
            fetchTwelveLabsEntities({ name: entityFilterText })
                .then(({ list }) => setEntities(list || []))
                .catch((err) => {
                    console.error('❌ HeirloomAISearchPage: entities fetch failed:', err);
                    setEntities([]);
                })
                .finally(() => setEntitiesLoading(false));
        }, 250);
        return () => clearTimeout(handle);
    }, [entityOpen, entityFilterText]);

    const openEntityPicker = () => {
        setEntityMentionStart(query.length);
        setEntityOpen(true);
        setCategoryOpen(false);
        setModelOpen(false);
        setLanguageOpen(false);
    };

    const handleQueryChange = (value) => {
        setQuery(value);
        if (value.endsWith('@')) {
            setEntityMentionStart(value.length - 1);
            setEntityOpen(true);
        } else if (entityMentionStart != null) {
            const afterAt = value.slice(entityMentionStart + 1);
            if (value[entityMentionStart] !== '@' || afterAt.includes(' ')) {
                setEntityOpen(false);
                setEntityMentionStart(null);
            }
        }
    };

    const insertEntity = (entity) => {
        const name = entity?.name || entity?.entity_name || entity?.label || 'entity';
        if (entityMentionStart != null) {
            setQuery(query.slice(0, entityMentionStart) + `@${name} `);
        }
        setEntityOpen(false);
        setEntityMentionStart(null);
        setTimeout(() => inputRef.current && inputRef.current.focus(), 50);
    };

    /* ── Image search ── */
    const handleImagesConfirm = (images) => {
        setSearchImages(images);
        setShowImagesModal(false);
    };

    const resetAdvanced = () => {
        setSearchOptions({ visual: true, audio: true, transcription: true });
        setTranscriptionOptions({ lexical: true, semantic: true });
        setOperator('or');
        setGroupBy('clip');
        setPageLimit(10);
        setRankRange([0, 100]);
        setFilter(emptyFilter());
    };

    const buildFilterPayload = () => {
        const f = {};
        const ids = filter.id.map(v => v.trim()).filter(Boolean);
        if (ids.length) f.id = ids;

        if (filter.duration.min !== '' || filter.duration.max !== '') {
            f.duration = {};
            if (filter.duration.min !== '') f.duration.min = Number(filter.duration.min);
            if (filter.duration.max !== '') f.duration.max = Number(filter.duration.max);
        }

        ['width', 'height', 'size'].forEach((key) => {
            const fld = filter[key];
            if (fld.useRange) {
                if (fld.min !== '' || fld.max !== '') {
                    f[key] = {};
                    if (fld.min !== '') f[key].min = Number(fld.min);
                    if (fld.max !== '') f[key].max = Number(fld.max);
                }
            } else if (fld.value !== '') {
                f[key] = Number(fld.value);
            }
        });

        if (filter.filename.trim()) f.filename = filter.filename.trim();
        return f;
    };

    /* ── Result-card actions — resolve the search hit back to its asset, then reuse
       the same routes/state shape as the content-grid card overlay buttons ── */
    const resolveAssetForItem = async (item) => {
        const assetId = item?.userMetadata?.assetId || item?.assetId || item?.videoId;
        if (!assetId) return null;
        const token = sessionStorage.getItem('token');
        try {
            const response = await axios.post(
                `${lambdaUrl}assetDetails?appname=${appname}&token=${encodeURIComponent(token)}&env=${environmentVariable}&assetid=${encodeURIComponent(assetId)}&appType=relay`,
                {},
                { headers: { 'Content-Type': 'application/json' } }
            );
            const asset = response.data?.result || response.data || {};
            if (!asset || !Object.keys(asset).length) return null;
            return { ...asset, assetid: asset.assetid || assetId };
        } catch (err) {
            console.error('❌ HeirloomAISearchPage: assetDetails failed:', err);
            return null;
        }
    };

    const withResolvedAsset = async (item, fn) => {
        const key = item?.videoId || item?.userMetadata?.assetId || item?.assetId;
        setResolvingId(key);
        const asset = await resolveAssetForItem(item);
        setResolvingId(null);
        if (!asset?.assetid) {
            showToast('Could not load the source asset for this clip.', 'error');
            return;
        }
        fn(asset);
    };

    const closeCaptionClick = (item) => withResolvedAsset(item, (asset) => {
        localStorage.setItem('previousDashboardRoute', window.location.pathname);
        localStorage.setItem('assetid', asset.assetid);
        browserHistory.push('/cc/' + asset.assetid);
    });

    const validateClick = (item) => withResolvedAsset(item, (asset) => {
        localStorage.setItem('previousDashboardRoute', window.location.pathname);
        localStorage.setItem('assetid', asset.assetid);
        const dataToPass = {
            sourcepath: asset?.video?.mp4?.proxyUrl ? asset?.video?.mp4?.proxyUrl : asset?.filepath ? `/${asset?.filepath}` : asset?.video?.mainVideo?.filePath ? `/${asset?.video?.mainVideo?.filePath}` : "",
            assetname: asset?.assetname || "",
            type: asset?.video?.mp4?.proxyUrl ? "proxy" : "source",
        };
        browserHistory.push(`/asset/validate/${asset.assetid}`, dataToPass);
    });

    const accurateEditClick = (item) => withResolvedAsset(item, (asset) => {
        localStorage.setItem('previousDashboardRoute', window.location.pathname);
        localStorage.setItem('assetid', asset.assetid);
        const dataToPass = {
            sourcepath: asset?.video?.mp4?.proxyUrl ? asset?.video?.mp4?.proxyUrl : asset?.filepath ? `${asset?.filepath}` : asset?.video?.mainVideo?.filePath ? `${asset?.video?.mainVideo?.filePath}` : "",
            assetName: asset?.assetname || "",
            spriteImage: asset?.spriteResponse?.spriteImage || "",
            spriteJson: asset?.spriteResponse?.spriteJson || "",
            thumbnail: asset?.thumbnail || defaultLandscape,
            accurateEdit: asset?.accurateEdit || false,
            assetid: asset?.assetid || "",
            waveform: asset?.waveformResponse?.waveform || null,
        };
        browserHistory.push(`/asset/acedit/${asset.assetid}`, dataToPass);
    });

    const posterClick = (item) => withResolvedAsset(item, (asset) => {
        localStorage.setItem('previousDashboardRoute', window.location.pathname);
        localStorage.setItem('assetid', asset.assetid);
        const dataToPass = {
            sourcepath: asset?.video?.mp4?.proxyUrl ? asset?.video?.mp4?.proxyUrl : asset?.filepath ? `/${asset?.filepath}` : asset?.video?.mainVideo?.filePath ? `/${asset?.video?.mainVideo?.filePath}` : "",
            assetName: asset?.assetname || "",
            spriteImage: asset?.spriteResponse?.spriteImage || "",
            spriteJson: asset?.spriteResponse?.spriteJson || "",
            thumbnail: asset?.thumbnail || defaultLandscape,
            accuratePoster: asset?.accuratePoster || false,
            assetid: asset?.assetid || "",
        };
        browserHistory.push(`/asset/poster/${asset.assetid}`, dataToPass);
    });

    const seeFullVideoClick = (item) => {
        const { indexId, videoId } = resolveTwelveLabsIds(item || {});
        const envoiAssetId = item?.userMetadata?.assetId || item?.assetId;
        if (!indexId || !videoId || !envoiAssetId) return;
        localStorage.setItem('previousDashboardRoute', '/ai-search');
        browserHistory.push(`/asset/twelvelabs/${indexId}/${videoId}/${envoiAssetId}`);
    };

    const handleSearch = async () => {
        if (!query.trim() && !selectedCats.length && !selectedModels.length && !selectedLanguages.length && !searchImages.length) return;

        setLoading(true);
        setSearchError('');
        setResults([]);
        setPageInfo(null);
        setHasSearched(true);
        setCategoryOpen(false);
        setModelOpen(false);
        setLanguageOpen(false);

        const searchOptionsList = Object.entries(searchOptions).filter(([, on]) => on).map(([k]) => k);
        const transcriptionOptionsList = searchOptions.transcription
            ? Object.entries(transcriptionOptions).filter(([, on]) => on).map(([k]) => k)
            : [];
        const filterPayload = buildFilterPayload();

        const payload = {
            searchText: query.trim(),
            ...(selectedLanguages.length ? { languages: selectedLanguages } : {}),
            ...(searchImages.length ? { images: searchImages.map((img) => img.url) } : {}),
            search_options: searchOptionsList,
            ...(transcriptionOptionsList.length ? { transcription_options: transcriptionOptionsList } : {}),
            operator,
            group_by: groupBy,
            ...(pageLimit !== '' ? { page_limit: Number(pageLimit) } : {}),
            rank: { min: rankRange[0], max: rankRange[1] },
            ...(Object.keys(filterPayload).length ? { filter: filterPayload } : {}),
        };

        try {
            const response = await axios.post(
                `${lambdaUrl}aisearch?appname=${appname}`,
                payload,
                { headers: { 'Content-Type': 'application/json' } }
            );

            const data = response.data;
            const list =
                data?.result?.data  ||
                data?.result        ||
                data?.results       ||
                data?.data          ||
                (Array.isArray(data) ? data : []);

            // TwelveLabs' search response only puts index_id on the top-level
            // search_pool, not on individual hits — attach it to every item so
            // resolveTwelveLabsIds() (used by the "See Full Video" button) can
            // find it. Without this, indexId is always undefined and the
            // player route never gets navigated to.
            const indexId =
                data?.result?.search_pool?.index_id ||
                data?.result?.pool?.index_id ||
                data?.result?.index_id ||
                data?.search_pool?.index_id ||
                data?.pool?.index_id ||
                data?.index_id ||
                null;

            const seen = new Set();
            const deduped = (Array.isArray(list) ? list : []).filter(item => {
                const key = `${Math.round(item.start)}_${Math.round(item.end)}_${item.transcription || ''}`;
                if (seen.has(key)) return false;
                seen.add(key);
                return true;
            }).map(item => (indexId && !item.indexId && !item.index_id ? { ...item, indexId } : item));
            setResults(deduped);
            setPageInfo(data?.result?.pageInfo || null);
        } catch (err) {
            console.error('AI Search failed:', err);
            setSearchError('Search failed. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    const handleClear = () => {
        setQuery('');
        setResults([]);
        setPageInfo(null);
        setSearchError('');
        setHasSearched(false);
        setCats([]);
        setModels([]);
        setLanguages([]);
        setSearchImages([]);
        setTimeout(() => inputRef.current && inputRef.current.focus(), 50);
    };

    const showSubmit = query.trim().length > 0 || selectedCats.length > 0 || selectedModels.length > 0 || selectedLanguages.length > 0 || searchImages.length > 0;

    const sepStyle = {
        flexShrink: 0,
        width: '1px',
        height: '26px',
        background: 'rgba(255,255,255,0.1)',
        alignSelf: 'center',
    };

    return (
        <>
            <Header />
            <div className="ai-search-page" style={{ height: 'calc(100vh - 70px)' }}>

            {/* ── Search header — centred initially, compact once results show ── */}
            <div className={`ai-search-header${hasSearched ? ' ai-search-header--active' : ''}`}>

                <h1 className="ai-heading">Where should we start?</h1>

                <div className="ai-pill-container">
                    <div className="ai-pill">

                        {/* Category */}
                        <div className="ai-dd-wrap" ref={categoryRef}>
                            <button
                                type="button"
                                className={`ai-dd-btn ai-cat-btn${selectedCats.length ? ' ai-dd-btn--on' : ''}`}
                                onClick={() => { setCategoryOpen(o => !o); setModelOpen(false); setLanguageOpen(false); }}
                                title="Category"
                            >
                                {selectedCats.length ? (
                                    <>
                                        <span className="ai-cat-label">
                                            {selectedCats.length === 1 ? selectedCats[0] : `Category (${selectedCats.length})`}
                                        </span>
                                        <span className="material-symbols-outlined ai-chev">
                                            {categoryOpen ? 'expand_less' : 'expand_more'}
                                        </span>
                                    </>
                                ) : (
                                    <span className="material-symbols-outlined ai-plus-icon">add</span>
                                )}
                            </button>

                            {categoryOpen && (
                                <div className="ai-menu ai-menu--left">
                                    <span className="ai-menu-label">Content Category</span>
                                    {categories.map(cat => (
                                        <button
                                            key={cat}
                                            type="button"
                                            className={`ai-menu-row${selectedCats.includes(cat) ? ' ai-menu-row--on' : ''}`}
                                            onClick={() => toggleCat(cat)}
                                        >
                                            <span className="ai-chk">
                                                {selectedCats.includes(cat) && <span className="material-symbols-outlined">check</span>}
                                            </span>
                                            {cat}
                                        </button>
                                    ))}
                                </div>
                            )}
                        </div>

                        <div style={sepStyle} />

                        {/* Input */}
                        <div
                            className="ai-input-wrap"
                            ref={entityRef}
                            style={{ position: 'relative' }}
                            onClick={() => inputRef.current && inputRef.current.focus()}
                        >
                            <input
                                ref={inputRef}
                                type="text"
                                className="ai-text-input"
                                placeholder={isListening ? 'Listening...' : 'Ask AI...'}
                                value={query}
                                onChange={(e) => handleQueryChange(e.target.value)}
                                onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                            />

                            {SHOW_IMAGE_AND_ENTITY_SEARCH && (
                                <>
                                    {searchImages.slice(0, 3).map((img) => (
                                        <img key={img.id} src={img.url} alt="" className="ai-input-image-thumb" />
                                    ))}
                                    {searchImages.length > 3 && (
                                        <span className="ai-input-image-more">+{searchImages.length - 3}</span>
                                    )}

                                    <button
                                        type="button"
                                        className="ai-input-icon-btn"
                                        title="Add images"
                                        onClick={(e) => { e.stopPropagation(); setShowImagesModal(true); }}
                                    >
                                        <span className="material-symbols-outlined">image</span>
                                    </button>
                                    <button
                                        type="button"
                                        className="ai-input-entity-btn"
                                        title="Mention an entity"
                                        onClick={(e) => { e.stopPropagation(); openEntityPicker(); }}
                                    >
                                        <span className="material-symbols-outlined">alternate_email</span>
                                        Entity
                                    </button>

                                    {entityOpen && (
                                        <div className="ai-menu ai-menu--left ai-menu--scroll ai-entity-menu" onClick={(e) => e.stopPropagation()}>
                                            <span className="ai-menu-label">
                                                {entityFilterText ? `Entities matching "${entityFilterText}"` : 'Entities'}
                                            </span>
                                            {entitiesLoading && (
                                                <div className="ai-entity-empty">Loading…</div>
                                            )}
                                            {!entitiesLoading && entities.length === 0 && (
                                                <div className="ai-entity-empty">No entities found</div>
                                            )}
                                            {!entitiesLoading && entities.map((entity, idx) => {
                                                const name = entity?.name || entity?.entity_name || entity?.label || 'Unnamed';
                                                const thumb = entity?.thumbnail_url || entity?.thumbnailUrl || entity?.image_url;
                                                return (
                                                    <button
                                                        key={entity?.id || `${name}-${idx}`}
                                                        type="button"
                                                        className="ai-menu-row"
                                                        onClick={() => insertEntity(entity)}
                                                    >
                                                        {thumb
                                                            ? <img src={thumb} alt={name} className="ai-entity-thumb" />
                                                            : <span className="material-symbols-outlined ai-entity-fallback-icon">face</span>
                                                        }
                                                        {name}
                                                    </button>
                                                );
                                            })}
                                        </div>
                                    )}
                                </>
                            )}
                        </div>

                        <div style={sepStyle} />

                        {/* AI Model */}
                        <div className="ai-dd-wrap" ref={modelRef}>
                            <button
                                type="button"
                                className={`ai-dd-btn ai-icon-dd-btn${selectedModels.length ? ' ai-dd-btn--on' : ''}`}
                                onClick={() => { setModelOpen(o => !o); setCategoryOpen(false); setLanguageOpen(false); }}
                                title="AI Model"
                            >
                                <span className="material-symbols-outlined">network_intel_node</span>
                                {selectedModels.length > 0 && (
                                    <span className="ai-dd-count">{selectedModels.length}</span>
                                )}
                                <span className="material-symbols-outlined ai-chev">
                                    {modelOpen ? 'expand_less' : 'expand_more'}
                                </span>
                            </button>

                            {modelOpen && (
                                <div className="ai-menu ai-menu--right">
                                    <span className="ai-menu-label">Platform &amp; Model</span>
                                    {AI_MODELS.map(({ platform, models }) => (
                                        <div key={platform}>
                                            <p className="ai-menu-platform">{platform}</p>
                                            {models.map(m => (
                                                <button
                                                    key={m}
                                                    type="button"
                                                    className={`ai-menu-row${selectedModels.includes(m) ? ' ai-menu-row--on' : ''}`}
                                                    onClick={() => toggleModel(m)}
                                                >
                                                    <span className="ai-chk">
                                                        {selectedModels.includes(m) && <span className="material-symbols-outlined">check</span>}
                                                    </span>
                                                    {m}
                                                </button>
                                            ))}
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>

                        <div style={sepStyle} />

                        {/* Language */}
                        <div className="ai-dd-wrap" ref={languageRef}>
                            <button
                                type="button"
                                className={`ai-dd-btn ai-icon-dd-btn${selectedLanguages.length ? ' ai-dd-btn--on' : ''}`}
                                onClick={() => { setLanguageOpen(o => !o); setCategoryOpen(false); setModelOpen(false); }}
                                title="Language"
                            >
                                <span className="material-symbols-outlined">language</span>
                                {selectedLanguages.length > 0 && (
                                    <span className="ai-dd-count">{selectedLanguages.length}</span>
                                )}
                                <span className="material-symbols-outlined ai-chev">
                                    {languageOpen ? 'expand_less' : 'expand_more'}
                                </span>
                            </button>

                            {languageOpen && (
                                <div className="ai-menu ai-menu--right ai-menu--scroll">
                                    <span className="ai-menu-label">Language</span>
                                    {Object.entries(LANGUAGES).sort((a, b) => a[1].localeCompare(b[1])).map(([code, name]) => (
                                        <button
                                            key={code}
                                            type="button"
                                            className={`ai-menu-row${selectedLanguages.includes(code) ? ' ai-menu-row--on' : ''}`}
                                            onClick={() => toggleLanguage(code)}
                                        >
                                            <span className="ai-chk">
                                                {selectedLanguages.includes(code) && <span className="material-symbols-outlined">check</span>}
                                            </span>
                                            {name}
                                        </button>
                                    ))}
                                </div>
                            )}
                        </div>

                        {/* Advanced search */}
                        <button
                            type="button"
                            className={`ai-adv-trigger${advancedOpen ? ' ai-adv-trigger--active' : ''}`}
                            onClick={() => { setAdvancedOpen(o => !o); setCategoryOpen(false); setModelOpen(false); setLanguageOpen(false); }}
                            aria-label="Advanced search"
                            title="Advanced Search"
                        >
                            <span className="material-symbols-outlined">tune</span>
                        </button>

                        {/* Mic */}
                        {micSupported && (
                            <button
                                type="button"
                                className={`ai-mic-btn${isListening ? ' ai-mic-btn--active' : ''}`}
                                onClick={toggleMic}
                                aria-label={isListening ? 'Stop listening' : 'Search by voice'}
                                title={isListening ? 'Stop' : 'Search by voice'}
                            >
                                <span className="material-symbols-outlined">mic</span>
                            </button>
                        )}

                        {/* Submit */}
                        {showSubmit && (
                            <button
                                type="button"
                                className={`ai-submit-btn${loading ? ' ai-submit-btn--loading' : ''}`}
                                onClick={handleSearch}
                                disabled={loading}
                                aria-label="Search"
                                title="Search"
                            >
                                {loading
                                    ? <span className="ai-spinner ai-spinner--sm" />
                                    : <span className="material-symbols-outlined">arrow_upward</span>
                                }
                            </button>
                        )}

                    </div>

                    <p className="ai-hint">
                        {loading
                            ? 'Searching...'
                            : isListening
                                ? "Speak now — we're listening..."
                                : 'Pick a category and AI model, then type or speak your query.'}
                    </p>
                </div>
            </div>

            {/* ── Advanced search drawer ── */}
            {advancedOpen && (
                <div className="ai-adv-overlay" onClick={() => setAdvancedOpen(false)}>
                    <div className="ai-adv-panel" onClick={(e) => e.stopPropagation()}>
                        <div className="ai-adv-header">
                            <h3>Advanced Search</h3>
                            <button type="button" className="ai-adv-close" onClick={() => setAdvancedOpen(false)}>
                                <span className="material-symbols-outlined">close</span>
                            </button>
                        </div>

                        <div className="ai-adv-body">

                            {/* Search options */}
                            <div className="ai-adv-field">
                                <div className="ai-adv-field-label">
                                    search_options <span className="ai-adv-tag">ARRAY</span>
                                    <span className="ai-adv-req">*</span>
                                </div>
                                <div className="ai-adv-checks">
                                    {['visual', 'audio', 'transcription'].map(key => (
                                        <label key={key} className="ai-adv-check">
                                            <input
                                                type="checkbox"
                                                checked={searchOptions[key]}
                                                onChange={() => setSearchOptions(p => ({ ...p, [key]: !p[key] }))}
                                            />
                                            {key.charAt(0).toUpperCase() + key.slice(1)}
                                        </label>
                                    ))}
                                </div>
                            </div>

                            {/* Transcription options */}
                            {searchOptions.transcription && (
                                <div className="ai-adv-field">
                                    <div className="ai-adv-field-label">
                                        transcription_options <span className="ai-adv-tag">ARRAY</span>
                                    </div>
                                    <div className="ai-adv-checks">
                                        {['lexical', 'semantic'].map(key => (
                                            <label key={key} className="ai-adv-check">
                                                <input
                                                    type="checkbox"
                                                    checked={transcriptionOptions[key]}
                                                    onChange={() => setTranscriptionOptions(p => ({ ...p, [key]: !p[key] }))}
                                                />
                                                {key.charAt(0).toUpperCase() + key.slice(1)}
                                            </label>
                                        ))}
                                    </div>
                                </div>
                            )}

                            <div className="ai-adv-divider" />

                            {/* Advanced settings */}
                            <div className="ai-adv-section-header">
                                <span>Advanced Settings</span>
                                <button type="button" className="ai-adv-reset" onClick={resetAdvanced}>
                                    <span className="material-symbols-outlined">restart_alt</span> Reset
                                </button>
                            </div>

                            <div className="ai-adv-field">
                                <div className="ai-adv-field-label">operator <span className="ai-adv-tag">ENUM</span></div>
                                <select className="ai-adv-select" value={operator} onChange={(e) => setOperator(e.target.value)}>
                                    <option value="or">OR</option>
                                    <option value="and">AND</option>
                                </select>
                            </div>

                            <div className="ai-adv-field">
                                <div className="ai-adv-field-label">group_by <span className="ai-adv-tag">ENUM</span></div>
                                <select className="ai-adv-select" value={groupBy} onChange={(e) => setGroupBy(e.target.value)}>
                                    <option value="clip">Clip</option>
                                    <option value="video">Video</option>
                                </select>
                            </div>

                            <div className="ai-adv-field">
                                <div className="ai-adv-field-label">page_limit <span className="ai-adv-tag">INTEGER</span></div>
                                <div className="ai-adv-input-clear">
                                    <input
                                        type="number"
                                        className="ai-adv-input"
                                        min={1}
                                        max={100}
                                        value={pageLimit}
                                        onChange={(e) => setPageLimit(e.target.value === '' ? '' : Number(e.target.value))}
                                    />
                                    {pageLimit !== '' && (
                                        <button type="button" className="ai-adv-clear-x" onClick={() => setPageLimit('')}>
                                            <span className="material-symbols-outlined">close</span>
                                        </button>
                                    )}
                                </div>
                            </div>

                            <div className="ai-adv-field">
                                <div className="ai-adv-field-label">rank <span className="ai-adv-tag">RANGE</span></div>
                                <div className="ai-adv-rank-values">
                                    <span>{rankRange[0]}</span>
                                    <span>{rankRange[1]}</span>
                                </div>
                                <DualRangeSlider min={0} max={100} value={rankRange} onChange={setRankRange} />
                            </div>

                            <div className="ai-adv-divider" />

                            {/* Filter */}
                            <button type="button" className="ai-adv-collapse-header" onClick={() => setFilterOpen(o => !o)}>
                                <span className="material-symbols-outlined">
                                    {filterOpen ? 'expand_more' : 'chevron_right'}
                                </span>
                                filter <span className="ai-adv-tag">OBJECT</span>
                            </button>

                            {filterOpen && (
                                <div className="ai-adv-filter-body">

                                    {/* id array */}
                                    <div className="ai-adv-field">
                                        <div className="ai-adv-field-label">id <span className="ai-adv-tag">ARRAY</span></div>
                                        {filter.id.map((val, idx) => (
                                            <div className="ai-adv-input-clear" key={idx}>
                                                <input
                                                    type="text"
                                                    className="ai-adv-input"
                                                    placeholder="Video ID"
                                                    value={val}
                                                    onChange={(e) => setFilter(p => ({
                                                        ...p,
                                                        id: p.id.map((v, i) => (i === idx ? e.target.value : v)),
                                                    }))}
                                                />
                                                <button
                                                    type="button"
                                                    className="ai-adv-clear-x"
                                                    onClick={() => setFilter(p => ({ ...p, id: p.id.filter((_, i) => i !== idx) }))}
                                                >
                                                    <span className="material-symbols-outlined">close</span>
                                                </button>
                                            </div>
                                        ))}
                                        <button
                                            type="button"
                                            className="ai-adv-add-item"
                                            onClick={() => setFilter(p => ({ ...p, id: [...p.id, ''] }))}
                                        >
                                            <span className="material-symbols-outlined">add</span> Add Item
                                        </button>
                                    </div>

                                    {/* duration */}
                                    <div className="ai-adv-field">
                                        <div className="ai-adv-field-label">duration <span className="ai-adv-tag">NUMBER</span></div>
                                        <div className="ai-adv-range-row">
                                            <input
                                                type="number"
                                                className="ai-adv-input"
                                                placeholder="min"
                                                value={filter.duration.min}
                                                onChange={(e) => setFilter(p => ({ ...p, duration: { ...p.duration, min: e.target.value } }))}
                                            />
                                            <span className="ai-adv-range-to">to</span>
                                            <input
                                                type="number"
                                                className="ai-adv-input"
                                                placeholder="max"
                                                value={filter.duration.max}
                                                onChange={(e) => setFilter(p => ({ ...p, duration: { ...p.duration, max: e.target.value } }))}
                                            />
                                        </div>
                                    </div>

                                    {/* width / height / size */}
                                    {[
                                        { key: 'width', placeholder: 'e.g. 1920' },
                                        { key: 'height', placeholder: 'e.g. 1080' },
                                        { key: 'size', placeholder: 'e.g. 10000000' },
                                    ].map(({ key, placeholder }) => (
                                        <div className="ai-adv-field" key={key}>
                                            <div className="ai-adv-field-label">{key} <span className="ai-adv-tag">NUMBER</span></div>
                                            {!filter[key].useRange ? (
                                                <div className="ai-adv-range-row">
                                                    <input
                                                        type="number"
                                                        className="ai-adv-input"
                                                        placeholder={placeholder}
                                                        value={filter[key].value}
                                                        onChange={(e) => setFilter(p => ({ ...p, [key]: { ...p[key], value: e.target.value } }))}
                                                    />
                                                    <button
                                                        type="button"
                                                        className="ai-adv-use-range"
                                                        onClick={() => setFilter(p => ({ ...p, [key]: { ...p[key], useRange: true } }))}
                                                    >
                                                        Use range
                                                    </button>
                                                </div>
                                            ) : (
                                                <div className="ai-adv-range-row">
                                                    <input
                                                        type="number"
                                                        className="ai-adv-input"
                                                        placeholder="min"
                                                        value={filter[key].min}
                                                        onChange={(e) => setFilter(p => ({ ...p, [key]: { ...p[key], min: e.target.value } }))}
                                                    />
                                                    <span className="ai-adv-range-to">to</span>
                                                    <input
                                                        type="number"
                                                        className="ai-adv-input"
                                                        placeholder="max"
                                                        value={filter[key].max}
                                                        onChange={(e) => setFilter(p => ({ ...p, [key]: { ...p[key], max: e.target.value } }))}
                                                    />
                                                    <button
                                                        type="button"
                                                        className="ai-adv-use-range"
                                                        onClick={() => setFilter(p => ({ ...p, [key]: { ...p[key], useRange: false } }))}
                                                    >
                                                        Use value
                                                    </button>
                                                </div>
                                            )}
                                        </div>
                                    ))}

                                    {/* filename */}
                                    <div className="ai-adv-field">
                                        <div className="ai-adv-field-label">filename <span className="ai-adv-tag">STRING</span></div>
                                        <input
                                            type="text"
                                            className="ai-adv-input"
                                            placeholder="e.g. my_video.mp4"
                                            value={filter.filename}
                                            onChange={(e) => setFilter(p => ({ ...p, filename: e.target.value }))}
                                        />
                                    </div>
                                </div>
                            )}
                        </div>

                        <div className="ai-adv-footer">
                            <button
                                type="button"
                                className="ai-adv-apply-btn"
                                onClick={() => { setAdvancedOpen(false); handleSearch(); }}
                            >
                                Apply &amp; Search
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* ── Results ── */}
            {hasSearched && (
                <div className="ai-results-scrollable">

                    {loading && (
                        <div className="ai-results-loading">
                            <span className="ai-spinner" />
                            <span>Searching...</span>
                        </div>
                    )}

                    {!loading && searchError && (
                        <div className="ai-results-error">
                            <span className="material-symbols-outlined">error</span>
                            {searchError}
                        </div>
                    )}

                    {!loading && !searchError && results.length === 0 && (
                        <div className="ai-results-empty">
                            <span className="material-symbols-outlined">search_off</span>
                            No results found{query ? <> for <strong>"{query}"</strong></> : ''}
                        </div>
                    )}

                    {!loading && results.length > 0 && (
                        <>
                            <div className="ai-results-header">
                                <span className="ai-results-count">
                                    {pageInfo?.totalResults
                                        ? `Showing ${results.length} of ${pageInfo.totalResults} results`
                                        : `${results.length} result${results.length !== 1 ? 's' : ''}`}
                                </span>
                                <button className="ai-results-clear" onClick={handleClear}>
                                    <span className="material-symbols-outlined">close</span>
                                    Clear
                                </button>
                            </div>

                            <div className="ai-results-grid">
                                {results.map((item, idx) => {
                                    const timeLabel = (item.start != null && item.end != null)
                                        ? `${formatTime(item.start)} – ${formatTime(item.end)}`
                                        : '';

                                    const itemKey = item.videoId || item.userMetadata?.assetId || item.assetId;
                                    const isResolving = resolvingId === itemKey;

                                    const cardKey = `${item.videoId}-${item.rank ?? idx}`;
                                    const isHovered = hoveredKey === cardKey;

                                    return (
                                        <div key={cardKey} className="ai-result-card">
                                            <div
                                                className="ai-result-thumb"
                                                onMouseEnter={() => handleThumbMouseEnter(cardKey)}
                                                onMouseLeave={() => handleThumbMouseLeave(cardKey)}
                                            >
                                                <img
                                                    src={item.thumbnailUrl || defaultLandscape}
                                                    alt={`Clip ${item.rank ?? idx}`}
                                                    onError={(e) => {
                                                        if (e.target.src !== defaultLandscape) e.target.src = defaultLandscape;
                                                    }}
                                                />

                                                <HoverClipPreview item={item} active={isHovered} />

                                                <div className="ai-result-top-right">
                                                    {item.rank != null && (
                                                        <span className="ai-result-rank">#{item.rank}</span>
                                                    )}
                                                    <div className="ai-result-menu-wrap">
                                                        <button
                                                            type="button"
                                                            className="ai-result-fullvideo-btn"
                                                            title="Settings"
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                setMenuOpenKey((k) => (k === cardKey ? null : cardKey));
                                                            }}
                                                        >
                                                            <span className="material-symbols-outlined">settings</span>
                                                        </button>
                                                        {menuOpenKey === cardKey && (
                                                            <div className="ai-result-menu" onClick={(e) => e.stopPropagation()}>
                                                                <button
                                                                    type="button"
                                                                    className="ai-result-menu-item"
                                                                    onClick={() => { setMenuOpenKey(null); seeFullVideoClick(item); }}
                                                                >
                                                                    <span className="material-symbols-outlined">movie_info</span>
                                                                    Info
                                                                </button>
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>

                                                {!isHovered && (
                                                    <div className="ai-result-overlay">
                                                        {/* <button
                                                            type="button"
                                                            className="video-card-btn"
                                                            title="See Full Video"
                                                            onClick={() => seeFullVideoClick(item)}
                                                        >
                                                            <span className="material-symbols-outlined">movie</span>
                                                        </button> */}
                                                        {/* <button
                                                            type="button"
                                                            className="video-card-btn"
                                                            title="Subtitles"
                                                            disabled={isResolving}
                                                            onClick={() => closeCaptionClick(item)}
                                                        >
                                                            <span className="material-symbols-outlined">subtitles</span>
                                                        </button>
                                                        <button
                                                            type="button"
                                                            className="video-card-btn"
                                                            title="Quality Control"
                                                            disabled={isResolving}
                                                            onClick={() => validateClick(item)}
                                                        >
                                                            <span className="material-symbols-outlined">data_check</span>
                                                        </button>
                                                        <button
                                                            type="button"
                                                            className="video-card-btn"
                                                            title="AccurateEdit"
                                                            disabled={isResolving}
                                                            onClick={() => accurateEditClick(item)}
                                                        >
                                                            <span className="material-symbols-outlined">movie_edit</span>
                                                        </button>
                                                        <button
                                                            type="button"
                                                            className="video-card-btn"
                                                            title="Poster"
                                                            disabled={isResolving}
                                                            onClick={() => posterClick(item)}
                                                        >
                                                            <span className="material-symbols-outlined">capture</span>
                                                        </button> */}
                                                    </div>
                                                )}

                                                {isResolving && (
                                                    <div className="ai-result-resolving">
                                                        <span className="ai-spinner ai-spinner--sm" />
                                                    </div>
                                                )}

                                                {!isHovered && (
                                                    <div className="ai-result-gradient">
                                                        {item?.transcription
                                                            ? <p className="ai-result-title">{item.transcription}</p>
                                                            : <p className="ai-result-title ai-result-title--muted">No transcription</p>
                                                        }
                                                        <div className="ai-result-meta-row">
                                                            {timeLabel && (
                                                                <span className="ai-result-time">
                                                                    <span className="material-symbols-outlined">schedule</span>
                                                                    {timeLabel}
                                                                </span>
                                                            )}
                                                            {item.videoId && (
                                                                <span className="ai-result-vid-badge">{item.videoId}</span>
                                                            )}
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    );
                                }
                                )}
                            </div>
                        </>
                    )}
                </div>
            )}

            {toastMsg && (
                <div
                    role="status"
                    style={{
                        position: 'fixed', bottom: 24, left: '50%', transform: 'translateX(-50%)',
                        background: toastMsg.type === 'error' ? '#dc2626' : '#16a34a',
                        color: '#fff', padding: '10px 18px', borderRadius: 8,
                        fontSize: 13, fontWeight: 600, zIndex: 500,
                        boxShadow: '0 8px 24px rgba(0,0,0,0.4)',
                    }}
                >
                    {toastMsg.message}
                </div>
            )}

            </div>
        </>
    );
}
